import { Loader2 } from "lucide-react";
import { useCandidateSkills } from "@/pages/candidate-profile/hooks/useCandidateProfile";
import type { ProfileSkillDetail } from "@/interface/profileSkill";

const getProficiencyDetails = (sk: ProfileSkillDetail) => {
  const rawProf = String(sk.proficiency || "").toLowerCase();

  if (rawProf === "beginner") return { level: "Beginner", percent: 25 };
  if (rawProf === "intermediate") return { level: "Intermediate", percent: 50 };
  if (rawProf === "advanced") return { level: "Advanced", percent: 75 };
  if (rawProf === "expert") return { level: "Expert", percent: 100 };

  const expYears = sk.experience_years;
  if (expYears !== undefined && expYears !== null && expYears !== 0) {
    const yrs = Number(expYears);
    if (!isNaN(yrs)) {
      if (yrs <= 1) return { level: "Beginner", percent: 25 };
      if (yrs <= 3) return { level: "Intermediate", percent: 50 };
      if (yrs <= 5) return { level: "Advanced", percent: 75 };
      return { level: "Expert", percent: 100 };
    }
  }

  if (rawProf) {
    const formatted = rawProf.charAt(0).toUpperCase() + rawProf.slice(1);
    return { level: formatted, percent: 75 };
  }

  return { level: "Proficient", percent: 75 };
};

export function SkillsTab({
  profileId,
  skills = [],
}: {
  profileId?: number | string;
  skills?: ProfileSkillDetail[];
}) {
  const {
    data: skillsQuery,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useCandidateSkills(profileId, { page: 1, page_size: 14 });

  const querySkills = skillsQuery?.pages?.flatMap((page: any) =>
    Array.isArray(page?.data) ? page.data : page?.data?.results || [],
  );

  const skillList: ProfileSkillDetail[] =
    querySkills && querySkills.length > 0
      ? querySkills
      : Array.isArray(skills)
        ? skills
        : [];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col max-h-[750px]">
      <h2 className="text-lg font-bold text-primary mb-6 border-b border-slate-100 dark:border-slate-800 pb-4 shrink-0">
        Skills & Expertise
      </h2>

      {isLoading && skillList.length === 0 ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="overflow-y-auto pr-1 flex-1 space-y-4"
        >
          {skillList.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No skills listed.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {skillList.map((sk: ProfileSkillDetail, i: number) => {
                const skillName = sk.skill_name || "Skill";
                const { level, percent } = getProficiencyDetails(sk);

                return (
                  <div
                    key={sk.id || i}
                    className="flex items-center gap-4 p-4 border border-slate-100 dark:border-slate-800 rounded-xl hover:border-primary/50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex justify-between mb-2">
                        <span className="font-bold text-sm text-slate-900 dark:text-white">
                          {skillName}
                        </span>
                        <span className="text-xs font-semibold text-primary">
                          {level}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all duration-1000"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="ml-2 text-xs text-slate-500 font-medium">
                Loading more skills...
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
